import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Printer, FileDown, Upload, FileCheck2, AlertTriangle, Loader2, BadgeCheck, Undo2,
} from "lucide-react";
import { api, extractError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { readPdf } from "../../lib/informeTributario/pdf";
import { detectType, parseCarpeta, parseTGR, reviveTGR } from "../../lib/informeTributario/parser";
import {
  analyze, defaultParams, sugerenciasERP, honorariosERPCompletos, resumenInforme, clp,
} from "../../lib/informeTributario/engine";
import { renderCliente, renderTrabajo, buildDocument, csvF29, titleCase, fmtRut } from "../../lib/informeTributario/render";
import ReportFrame from "./ReportFrame";
import PanelERP from "./PanelERP";
import PanelValidaciones from "./PanelValidaciones";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const normRut = (r) => (r || "").replace(/[^0-9kK]/g, "").toUpperCase();
const fechaValida = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "") && !isNaN(new Date(`${s}T12:00:00`));

const SECCIONES = [
  {
    id: "ident",
    titulo: "Identificación del informe",
    campos: [
      ["estudio", "Estudio"],
      ["asesor", "Asesor responsable"],
      ["fechaCorte", "Fecha de corte", "date"],
      ["anio", "Año comercial", "number", { min: 2020, max: 2035 }],
    ],
  },
  {
    id: "trib",
    titulo: "Parámetros tributarios",
    campos: [
      ["tasaIDPC", "Tasa primera categoría (%)", "number", { step: 0.1 }],
      ["ppmLegal", "PPM mínimo legal (%)", "number", { step: 0.005 }],
      ["tasaPPMFutura", "Tasa PPM meses restantes (%)", "number", { step: 0.005 }],
      ["tasaRetHon", "Retención honorarios (%)", "number", { step: 0.05 }],
      ["uf", "Valor UF ($)", "number", { step: 0.01 }],
      ["mesesPromedio", "Meses para promediar", "number", { min: 1, max: 12 }],
      ["tasaInteresAnual", "Interés por mora anual (%)", "number", { step: 0.01 }],
      ["ipcProyectado", "IPC mensual estimado (%)", "number", { step: 0.1 }],
    ],
    checks: [["afComoGasto", "Activo fijo como gasto (depreciación instantánea Pro Pyme)"]],
    hint: "Valores sugeridos según régimen y año: Pro Pyme General 12,5% (años comerciales 2025 a 2027) y PPM 0,125% (Ley 21.755, Circular SII N°53/2025). Interés por mora: 7,70% anual (Res. Ex. SII N°88/2026). El IPC estimado solo se usa en los meses aún no publicados al reajustar los PPM. Revisa antes de emitir.",
  },
  {
    id: "gastos",
    titulo: "Gastos y ajustes que el F29 no muestra",
    campos: [
      ["remuneraciones", "Remuneraciones y leyes sociales del año ($)", "number", { step: 1000 }],
      ["gastosSinIVA", "Otros gastos sin IVA del año ($)", "number", { step: 1000 }],
      ["agregados", "Agregados a la base ($)", "number", { step: 1000 }],
      ["deducciones", "Deducciones a la base ($)", "number", { step: 1000 }],
      ["otrosCreditos", "Otros créditos contra el impuesto ($)", "number", { step: 1000 }],
    ],
    hint: "Montos anuales: arriendos exentos, cotizaciones, seguros, intereses, patente, gastos rechazados, etc. Los datos de Aequor los completan automáticamente.",
  },
  {
    id: "mejoras",
    titulo: "Escenario con mejoras",
    campos: [
      ["pct14E", "Utilidad que se reinvierte (14 E, máx. 50%)", "number", { min: 0, max: 50 }],
      ["retirosEstimados", "Retiros de socios estimados ($)", "number", { step: 1000 }],
      ["compraAFPlan", "Compra de activo fijo antes del 31-12 ($)", "number", { step: 1000 }],
      ["gastosAnticipados", "Gastos adelantados antes del 31-12 ($)", "number", { step: 1000 }],
    ],
  },
  {
    id: "conta",
    titulo: "Prebalance (opcional)",
    campos: [
      ["caja", "Caja y bancos", "number"],
      ["clientes", "Clientes por cobrar", "number"],
      ["activoFijoNeto", "Activo fijo neto", "number"],
      ["proveedores", "Proveedores por pagar", "number"],
      ["prestamos", "Préstamos", "number"],
      ["capitalPagado", "Capital pagado", "number"],
      ["ventasContables", "Ventas según contabilidad", "number"],
      ["gastosContables", "Gastos según contabilidad", "number"],
    ],
    hint: "Si los completas, el informe agrega un prebalance resumido y la conciliación de ventas contables con los F29.",
  },
  {
    id: "coment",
    titulo: "Comentarios del asesor",
    textos: [
      ["lecturaAsesor", "Lectura del asesor (resultados)", "Opcional. Se suma a la lectura automática."],
      ["comentarioFinal", "Recomendación principal", "2 o 3 frases para el cliente."],
      ["notasInternas", "Notas internas (solo en la hoja de trabajo)", ""],
    ],
  },
];

const inputCls =
  "w-full mt-1 px-2.5 py-1.5 border border-border rounded-sm text-[13px] bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:opacity-60";

function Bloque({ titulo, children, defaultOpen = false, testid }) {
  return (
    <details open={defaultOpen} className="group border border-border rounded-sm bg-card" data-testid={testid}>
      <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center justify-between text-[12px] font-semibold tracking-wide text-foreground select-none">
        {titulo}
        <span className="text-muted-foreground group-open:rotate-45 transition-transform text-base leading-none">+</span>
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  );
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ultimoMesDeclarado(C, anio) {
  const meses = (C?.f29 || []).filter((f) => f.declarado && f.anio === anio).map((f) => f.mes);
  return meses.length ? Math.max(...meses) : 0;
}

const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fechaCL = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : s || "");

function resumenIPC(ipc) {
  const meses = Object.entries(ipc || {}).sort(([a], [b]) => a.localeCompare(b));
  if (!meses.length) return "sin IPC publicado para el año";
  const deducidos = meses.filter(([, v]) => v?.fuente === "UTM").length;
  const ultimo = +meses[meses.length - 1][0].slice(5, 7);
  return `IPC de ene a ${MES_CORTO[ultimo - 1]}${deducidos ? ` (${deducidos} ${deducidos === 1 ? "mes deducido" : "meses deducidos"} de la UTM)` : ""}`;
}

function descargar(contenido, nombre, tipo) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function EditorInforme({ informeId, empresaActiva, readOnly, onVolver, onGuardado }) {
  const { user } = useAuth();
  const [id, setId] = useState(informeId || null);
  const [C, setC] = useState(null);
  const [T, setT] = useState(null);
  const [P, setPRaw] = useState(null);
  const [X, setX] = useState(null);
  const [estado, setEstado] = useState("borrador");
  const [archivos, setArchivos] = useState({ carpeta: "", tgr: "" });
  const [cargando, setCargando] = useState(!!informeId);
  const [leyendo, setLeyendo] = useState(false);
  const [msg, setMsg] = useState("");
  const [arrastrando, setArrastrando] = useState(false);
  const [erpLoading, setErpLoading] = useState(false);
  const [erpError, setErpError] = useState("");
  const [indLoading, setIndLoading] = useState(false);
  const [indError, setIndError] = useState("");
  const [vista, setVista] = useState("cliente");
  const [guardando, setGuardando] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmEmitir, setConfirmEmitir] = useState(false);
  const frameRef = useRef(null);
  const fileRef = useRef(null);

  const setP = useCallback((fn) => {
    setPRaw(fn);
    setDirty(true);
  }, []);

  useEffect(() => {
    if (!informeId) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/informes-tributarios/${informeId}`);
        if (!vivo) return;
        setC(data.carpeta);
        setT(reviveTGR(data.tgr));
        setPRaw(data.params);
        setX(data.datos_erp || null);
        setEstado(data.estado || "borrador");
        setArchivos({
          carpeta: `Carpeta tributaria generada el ${data.carpeta?.fechaGeneracion || "—"}`,
          tgr: data.tgr ? `Certificado TGR emitido el ${data.tgr.emision || "—"}` : "",
        });
      } catch (e) {
        toast.error(extractError(e));
        onVolver();
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [informeId]);

  const rutDistinto = !!(C && empresaActiva?.rut && normRut(C.rut) !== normRut(empresaActiva.rut));

  const cargarERP = useCallback(async (anio, hasta) => {
    setErpLoading(true);
    setErpError("");
    try {
      const { data } = await api.get("/informes-tributarios/datos-erp", { params: { anio, hasta } });
      setX(data);
    } catch (e) {
      setErpError(extractError(e));
    } finally {
      setErpLoading(false);
    }
  }, []);

  const anio = P ? +P.anio : null;
  const fechaCorte = P?.fechaCorte;
  useEffect(() => {
    if (!C || !anio || !fechaValida(fechaCorte) || rutDistinto) return;
    if (X && X.anio === anio && X.hasta === fechaCorte) return;
    const t = setTimeout(() => cargarERP(anio, fechaCorte), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [C, anio, fechaCorte, rutDistinto]);

  const cargarIndicadores = useCallback(async (fecha, anioInd) => {
    setIndLoading(true);
    setIndError("");
    try {
      const { data } = await api.get("/informes-tributarios/indicadores", { params: { fecha, anio: anioInd } });
      setP((prev) => ({
        ...prev,
        ...(data.uf ? { uf: data.uf } : {}),
        ipc: data.ipc || {},
        indicadores: { fecha, anio: anioInd, ufFecha: data.uf_fecha, utm: data.utm, fuente: data.fuente, consultado: data.consultado },
      }));
    } catch (e) {
      setIndError(extractError(e));
    } finally {
      setIndLoading(false);
    }
  }, [setP]);

  // Informes nuevos o con otra fecha de corte: UF del día e IPC del año. Los informes anteriores a esta
  // función (sin "indicadores") conservan sus valores hasta que se actualicen a mano.
  const indicadoresP = P?.indicadores;
  useEffect(() => {
    if (!C || readOnly || !anio || !fechaValida(fechaCorte) || indicadoresP === undefined) return;
    if (indicadoresP && indicadoresP.fecha === fechaCorte && indicadoresP.anio === anio) return;
    const t = setTimeout(() => cargarIndicadores(fechaCorte, anio), 600);
    return () => clearTimeout(t);
  }, [C, readOnly, anio, fechaCorte, indicadoresP, cargarIndicadores]);

  // Primera vez que llegan datos del ERP para este informe: se aplican como punto de partida.
  useEffect(() => {
    if (!X || !C || !P || P.erpInicializado) return;
    const a = +P.anio;
    const S0 = sugerenciasERP(X, { anio: a, ultimoMes: ultimoMesDeclarado(C, a) });
    const patch = { erpInicializado: true, gastosSel: S0.gastos.seleccion };
    const aplicados = [];
    if (honorariosERPCompletos(C, X, a)) {
      patch.fuenteHonorarios = "erp";
      aplicados.push("honorarios");
    }
    if (S0.hay.rem && !+P.remuneraciones) {
      patch.remuneraciones = S0.remuneraciones.anual;
      aplicados.push("remuneraciones");
    }
    if (S0.contabilidad) {
      if (!+P.gastosSinIVA && S0.gastos.anual) {
        patch.gastosSinIVA = S0.gastos.anual;
        aplicados.push("gastos sin IVA");
      }
      const k = ["caja", "clientes", "activoFijoNeto", "proveedores", "prestamos", "capitalPagado", "ventasContables", "gastosContables"];
      if (k.every((x) => P[x] === "" || P[x] === undefined || P[x] === null)) {
        const c = S0.contabilidad;
        Object.assign(patch, {
          caja: c.caja, clientes: c.clientes, activoFijoNeto: c.activoFijoNeto, proveedores: c.proveedores,
          prestamos: c.prestamos, capitalPagado: c.capitalPagado, ventasContables: c.ventas, gastosContables: c.gastos,
        });
        aplicados.push("prebalance");
      }
    }
    setP((prev) => ({ ...prev, ...patch }));
    if (aplicados.length) toast.success(`Se tomaron de Aequor: ${aplicados.join(", ")}.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [X]);

  const procesarArchivos = async (files) => {
    setMsg("");
    setLeyendo(true);
    try {
      for (const file of files) {
        if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
          setMsg(`"${file.name}" no es un PDF.`);
          continue;
        }
        const pages = await readPdf(file);
        const tipo = detectType(pages);
        if (!tipo) {
          setMsg(`No reconozco "${file.name}" como carpeta tributaria del SII ni como certificado de deuda de la TGR.`);
          continue;
        }
        if (tipo === "carpeta") {
          const c = parseCarpeta(pages);
          const declarados = c.f29.filter((f) => f.declarado).length;
          if (!declarados) setMsg("La carpeta no trae declaraciones F29. Genera la carpeta tributaria con el detalle de IVA.");
          const dp = defaultParams(c);
          setC(c);
          setPRaw((prev) => (prev ? { ...prev, fechaCorte: dp.fechaCorte, anio: dp.anio } : { ...dp, asesor: user?.name || "" }));
          setArchivos((a) => ({ ...a, carpeta: `${file.name} · ${declarados} F29 leídos` }));
        } else {
          const t = parseTGR(pages);
          setT(t);
          setArchivos((a) => ({ ...a, tgr: `${file.name} · ${t.morosa.length + t.noVencida.length + t.convenio.length} giros leídos` }));
        }
        setDirty(true);
      }
    } catch (e) {
      console.error(e);
      setMsg(e?.message || "No se pudo leer el archivo.");
    } finally {
      setLeyendo(false);
    }
  };

  const tgrRutDistinto = !!(C && T?.rut && normRut(T.rut) !== normRut(C.rut));

  const Pd = useDebounced(P, 250);
  const M = useMemo(() => {
    if (!C || !Pd || !fechaValida(Pd.fechaCorte) || !+Pd.anio) return null;
    try {
      return analyze(C, T, Pd, rutDistinto ? null : X);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [C, T, Pd, X, rutDistinto]);

  const S = useMemo(
    () => (X && P && !rutDistinto ? sugerenciasERP(X, { anio: +P.anio, ultimoMes: M?.ultimoMes, gastosSel: P.gastosSel }) : null),
    [X, P, M?.ultimoMes, rutDistinto]
  );

  const nombre = C ? titleCase(C.razonSocial) : "";
  const docTitle = C && P ? `${vista === "trabajo" ? "Hoja de Trabajo" : "Informe Tributario"} – ${nombre} – ${P.fechaCorte}` : "Informe tributario";
  const html = useMemo(() => {
    if (!M) return null;
    const pie = vista === "trabajo"
      ? `Hoja de trabajo (uso interno) · ${nombre} · corte ${M.P.fechaCorte}`
      : `Informe tributario integral · ${nombre} · RUT ${fmtRut(M.C.rut)}`;
    return buildDocument({ body: vista === "trabajo" ? renderTrabajo(M) : renderCliente(M), title: docTitle, kind: vista, pie });
  }, [M, vista, nombre, docTitle]);

  const guardar = async (nuevoEstado = estado) => {
    if (!C || !P || !fechaValida(P.fechaCorte)) return;
    setGuardando(true);
    try {
      const Xg = rutDistinto ? null : X;
      const payload = {
        fecha_corte: P.fechaCorte,
        anio: +P.anio,
        rut: C.rut,
        razon_social: C.razonSocial,
        carpeta: C,
        tgr: T,
        params: P,
        datos_erp: Xg,
        resumen: resumenInforme(analyze(C, T, P, Xg)),
        estado: nuevoEstado,
      };
      const { data } = id
        ? await api.put(`/informes-tributarios/${id}`, payload)
        : await api.post("/informes-tributarios", payload);
      setId(data.id);
      setEstado(data.estado);
      setDirty(false);
      toast.success(nuevoEstado === "emitido" ? "Informe emitido y guardado en el historial" : "Informe guardado");
      onGuardado?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setGuardando(false);
    }
  };

  const bloqueos = M?.bloqueos || [];
  const bloqueado = bloqueos.length > 0;
  const motivoBloqueo = bloqueado ? `No se puede emitir: ${bloqueos.map((v) => `${v.id} ${v.control}`).join(" · ")}` : "";

  const emitir = () => {
    if (bloqueado) {
      toast.error(motivoBloqueo);
      return;
    }
    if (!+P?.remuneraciones && !+P?.gastosSinIVA) setConfirmEmitir(true);
    else guardar("emitido");
  };

  const exportarE = () => {
    if (!M?.E) return;
    const E = { ...M.E, generadoEn: new Date().toISOString() };
    descargar(JSON.stringify(E, null, 2), `ERP_${E.rut || "empresa"}_${E.fechaCorte}.json`, "application/json;charset=utf-8");
  };

  const volver = () => {
    if (dirty && !readOnly && !window.confirm("Hay cambios sin guardar. ¿Salir de todas formas?")) return;
    onVolver();
  };

  if (cargando) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Abriendo informe…
      </div>
    );
  }

  const zonaArchivos = (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        procesarArchivos([...e.dataTransfer.files]);
      }}
      onClick={() => fileRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
      className={`cursor-pointer rounded-sm border-2 border-dashed px-4 py-4 text-center transition-colors ${
        arrastrando ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 hover:bg-muted/40"
      }`}
      data-testid="informe-dropzone"
    >
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          procesarArchivos([...e.target.files]);
          e.target.value = "";
        }}
      />
      {leyendo ? (
        <div className="flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Leyendo PDF…
        </div>
      ) : (
        <>
          <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" strokeWidth={1.75} />
          <div className="text-[13px] font-medium text-foreground">Arrastra o elige los PDF</div>
          <div className="text-[11.5px] text-muted-foreground">Carpeta tributaria (SII) y certificado de deuda (TGR)</div>
        </>
      )}
    </div>
  );

  const archivoCargado = (texto, etiqueta) => (
    <div className={`flex items-start gap-2 text-[12px] ${texto ? "text-foreground" : "text-muted-foreground"}`}>
      {texto ? <FileCheck2 className="h-4 w-4 text-emerald-600 shrink-0 mt-px" /> : <span className="h-4 w-4 shrink-0 rounded-full border border-dashed border-border" />}
      <span className="min-w-0 break-words">{texto || `${etiqueta}: no cargado`}</span>
    </div>
  );

  if (!C) {
    return (
      <div className="max-w-2xl mx-auto py-6">
        <button type="button" onClick={volver} className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4" /> Volver al historial
        </button>
        <div className="border border-border rounded-sm bg-card p-6 space-y-4">
          <div>
            <h2 className="font-display text-2xl text-foreground">Nuevo informe tributario</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Sube la carpeta tributaria del SII de {empresaActiva?.nombre || "la empresa"} y, si la tienes, el certificado de deuda de la TGR.
              Los PDF se leen en este navegador; luego se cruzan con los honorarios, remuneraciones, libro mayor y registro de compras y ventas de Aequor.
            </p>
          </div>
          {zonaArchivos}
          {archivoCargado(archivos.tgr, "Certificado TGR")}
          {msg && <p className="text-[12px] rounded-sm bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-3 py-2">{msg}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="min-w-0">
          <button type="button" onClick={volver} className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground mb-1" data-testid="informe-volver">
            <ArrowLeft className="h-3.5 w-3.5" /> Historial
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl text-foreground truncate">{nombre}</h2>
            <span
              className={`text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-0.5 rounded-sm ${
                estado === "emitido" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-muted text-muted-foreground"
              }`}
            >
              {estado === "emitido" ? "Emitido" : "Borrador"}
            </span>
            {dirty && !readOnly && <span className="text-[11px] text-amber-700 dark:text-amber-400">Cambios sin guardar</span>}
          </div>
          <div className="text-[12px] text-muted-foreground font-mono">RUT {fmtRut(C.rut)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-sm border border-border p-0.5 bg-muted/40" role="tablist">
            {[["cliente", "Informe al cliente"], ["trabajo", "Hoja de trabajo"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={vista === v}
                onClick={() => setVista(v)}
                className={`px-3 py-1.5 text-[12px] rounded-sm transition-colors ${vista === v ? "bg-card text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`informe-vista-${v}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => frameRef.current?.print()}
            disabled={!html || (vista === "cliente" && bloqueado)}
            className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted disabled:opacity-50"
            title={vista === "cliente" && bloqueado ? `${motivoBloqueo}. La hoja de trabajo sí se puede imprimir.` : "En el diálogo de impresión elige «Guardar como PDF»"}
            data-testid="informe-imprimir"
          >
            <Printer className="h-4 w-4" /> PDF
          </button>
          <button
            type="button"
            onClick={() => M && descargar(csvF29(M), `F29_${C.rut}_${M.anio}.csv`, "text/csv;charset=utf-8")}
            disabled={!M}
            className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted disabled:opacity-50"
            title="Detalle mensual de F29 en CSV"
          >
            <FileDown className="h-4 w-4" /> CSV
          </button>
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={() => guardar()}
                disabled={guardando || rutDistinto || !M}
                className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted disabled:opacity-50"
                data-testid="informe-guardar"
              >
                {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
              </button>
              {estado === "emitido" ? (
                <button
                  type="button"
                  onClick={() => guardar("borrador")}
                  disabled={guardando || rutDistinto || !M}
                  className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-2 rounded-sm hover:bg-muted disabled:opacity-50"
                >
                  <Undo2 className="h-4 w-4" /> Volver a borrador
                </button>
              ) : (
                <button
                  type="button"
                  onClick={emitir}
                  disabled={guardando || rutDistinto || !M || bloqueado}
                  title={motivoBloqueo || undefined}
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-3 py-2 rounded-sm disabled:opacity-60"
                  data-testid="informe-emitir"
                >
                  <BadgeCheck className="h-4 w-4" /> Emitir
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {rutDistinto && (
        <div className="flex items-start gap-2 rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive" data-testid="informe-rut-distinto">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Esta carpeta es del RUT {fmtRut(C.rut)}, pero la empresa activa es {empresaActiva?.nombre} (RUT {empresaActiva?.rut}).
            Cambia la empresa activa para guardar el informe y cruzarlo con sus registros.
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[350px_minmax(0,1fr)] items-start">
        <aside className="space-y-2.5 lg:sticky lg:top-0 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-1" data-testid="informe-panel">
          <Bloque titulo="Documentos del cliente" defaultOpen>
            <div className="space-y-2">
              {archivoCargado(archivos.carpeta, "Carpeta tributaria")}
              {archivoCargado(archivos.tgr, "Certificado TGR")}
              {tgrRutDistinto && (
                <p className="text-[11.5px] text-destructive">El RUT del certificado TGR ({T.rut}) no coincide con el de la carpeta.</p>
              )}
              {!readOnly && zonaArchivos}
              {msg && <p className="text-[11.5px] rounded-sm bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 px-2.5 py-2">{msg}</p>}
            </div>
          </Bloque>

          {M && (
            <Bloque
              titulo={`Validaciones antes de emitir${bloqueado ? ` · ${bloqueos.length} ${bloqueos.length === 1 ? "bloqueo" : "bloqueos"}` : ""}`}
              defaultOpen
              testid="informe-bloque-validaciones"
            >
              <PanelValidaciones M={M} P={P} setP={setP} X={rutDistinto ? null : X} readOnly={readOnly} onExportar={exportarE} />
            </Bloque>
          )}

          <Bloque titulo="Datos de Aequor (ERP)" defaultOpen testid="informe-bloque-erp">
            <PanelERP
              X={X}
              S={S}
              M={M}
              P={P}
              setP={setP}
              loading={erpLoading}
              error={erpError}
              bloqueado={rutDistinto}
              readOnly={readOnly}
              onRefresh={() => fechaValida(P.fechaCorte) && cargarERP(+P.anio, P.fechaCorte)}
            />
          </Bloque>

          {SECCIONES.map((sec) => (
            <Bloque key={sec.id} titulo={sec.titulo} defaultOpen={sec.id === "gastos"}>
              {sec.campos && (
                <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
                  {sec.campos.map(([name, label, type = "text", extra = {}]) => (
                    <label key={name} className={`block text-[11.5px] text-muted-foreground ${type === "text" ? "col-span-2" : ""}`}>
                      {label}
                      <input
                        type={type}
                        value={P[name] ?? ""}
                        disabled={readOnly}
                        onChange={(e) => {
                          const v = e.target.value;
                          setP((prev) => ({ ...prev, [name]: v }));
                        }}
                        className={inputCls}
                        {...extra}
                      />
                    </label>
                  ))}
                </div>
              )}
              {sec.checks?.map(([name, label]) => (
                <label key={name} className="flex items-center gap-2 text-[12px] text-foreground mt-2.5">
                  <input
                    type="checkbox"
                    checked={!!P[name]}
                    disabled={readOnly}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setP((prev) => ({ ...prev, [name]: v }));
                    }}
                  />
                  {label}
                </label>
              ))}
              {sec.textos?.map(([name, label, ph]) => (
                <label key={name} className="block text-[11.5px] text-muted-foreground mb-2">
                  {label}
                  <textarea
                    rows={3}
                    value={P[name] ?? ""}
                    placeholder={ph}
                    disabled={readOnly}
                    onChange={(e) => {
                      const v = e.target.value;
                      setP((prev) => ({ ...prev, [name]: v }));
                    }}
                    className={inputCls}
                  />
                </label>
              ))}
              {sec.id === "trib" && (
                <div className="mt-2.5 rounded-sm bg-muted/50 px-2.5 py-2 text-[11px] text-muted-foreground leading-snug" data-testid="informe-indicadores">
                  {indLoading ? (
                    <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Consultando UF e IPC…</span>
                  ) : P.indicadores ? (
                    <span>
                      UF del {fechaCL(P.indicadores.ufFecha)} · {resumenIPC(P.ipc)}. Fuente: {P.indicadores.fuente}.
                    </span>
                  ) : (
                    <span className={indError ? "text-amber-700 dark:text-amber-400" : ""}>
                      {indError || "UF e IPC sin consultar: el reajuste de los PPM usa una aproximación de 1%."}
                    </span>
                  )}
                  {!readOnly && !indLoading && fechaValida(P.fechaCorte) && (
                    <button
                      type="button"
                      onClick={() => cargarIndicadores(P.fechaCorte, +P.anio)}
                      className="block mt-1 text-primary hover:underline"
                      data-testid="informe-indicadores-actualizar"
                    >
                      {P.indicadores ? "Volver a consultar" : "Consultar UF e IPC"}
                    </button>
                  )}
                </div>
              )}
              {sec.hint && <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{sec.hint}</p>}
              {sec.id === "gastos" && M && (
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Renta proyectada: <b className="text-foreground">{M.esc.actual.saldo > 0 ? clp(M.esc.actual.saldo) : `devolución ${clp(-M.esc.actual.saldo)}`}</b>
                </p>
              )}
            </Bloque>
          ))}
        </aside>

        <section className="min-w-0 rounded-sm border border-border bg-muted/30 overflow-hidden">
          {html ? (
            <ReportFrame ref={frameRef} html={html} title={docTitle} />
          ) : (
            <div className="py-24 text-center text-sm text-muted-foreground">
              {!fechaValida(P?.fechaCorte) ? "Ingresa una fecha de corte válida para ver el informe." : "Preparando el informe…"}
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={confirmEmitir} onOpenChange={setConfirmEmitir}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Emitir sin sueldos ni gastos sin IVA?</AlertDialogTitle>
            <AlertDialogDescription>
              El resultado y la renta proyectada solo consideran lo declarado en los F29 y los honorarios.
              Si la empresa tiene trabajadores, arriendos, seguros, intereses u otros gastos sin factura, la renta quedará sobrestimada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Revisar primero</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmEmitir(false);
                guardar("emitido");
              }}
            >
              Emitir de todas formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
